import React from 'react';
import { formatNumber } from '../../util/helper';

const TD_CLASS = 'border border-collapse border-gray-100 text-left p-1 text-xs md:text-sm';

/** 가격 + 날짜 서브라인 셀 */
function PriceWithDateCell({ row, column, dateColumn, colIndex }) {
  return (
    <td key={colIndex} className={`${TD_CLASS} whitespace-nowrap`}>
      <div>{row[column] !== null ? formatNumber(row[column]) : '-'}</div>
      {row[dateColumn] && (
        <div className="text-xs text-gray-500">({row[dateColumn]})</div>
      )}
    </td>
  );
}

/** 매물 리스트 확인 버튼 셀 */
function SaleButtonsCell({ row, colIndex, onSaleClick }) {
  const saleCount = row['sale_count'] || 0;
  const jeonseCount = row['jeonse_count'] || 0;
  return (
    <td key={colIndex} className="border border-collapse border-gray-100 text-left p-1">
      <div className="flex space-x-1">
        <button
          type="button"
          onClick={() => onSaleClick(row['apartment_number'], row['size_no'], false, row['size_type'])}
          className="p-1 border border-gray-100 bg-orange-100 hover:bg-orange-200 text-black rounded-md flex-1 text-sm whitespace-nowrap text-center underline"
        >
          매매({saleCount})
        </button>
        <button
          type="button"
          onClick={() => onSaleClick(row['apartment_number'], row['size_no'], true, row['size_type'])}
          className="p-1 border border-gray-100 bg-gray-100 hover:bg-gray-300 text-black rounded-md flex-1 text-sm whitespace-nowrap text-center underline"
        >
          전세({jeonseCount})
        </button>
      </div>
    </td>
  );
}

/** 아파트명 링크 셀 */
function ApartmentNameCell({ row, column, colIndex, navigate }) {
  return (
    <td key={colIndex} className={`${TD_CLASS} min-w-[150px] w-[150px]`}>
      <div
        className="break-words text-gray-600 underline hover:text-green-600 font-semibold cursor-pointer"
        onClick={() => navigate(`/apartment/${row['apartment_number']}`)}
      >
        {row[column]}
      </div>
    </td>
  );
}

/** 동 이름 + 지하철 툴팁 셀 */
function SubdistrictCell({ row, column, colIndex, handleShowSubwayInfo }) {
  return (
    <td key={colIndex} className={TD_CLASS}>
      {row.subwaystationname ? (
        <div
          className="text-black underline hover:text-green-600 cursor-pointer"
          onClick={(e) => handleShowSubwayInfo(e, row)}
        >
          {row[column]}
        </div>
      ) : (
        row[column]
      )}
    </td>
  );
}

/** 전고점대비 퍼센트 셀 */
function PeakComparisonCell({ row, column, colIndex }) {
  const percentage = row[column];
  const priceDifference = formatNumber(row['current_vs_peak_price']);
  return (
    <td key={colIndex} className={TD_CLASS}>
      <div>{percentage !== null ? `${percentage}%` : '-'}</div>
      <div className="text-xs text-gray-500">{priceDifference}</div>
    </td>
  );
}

/** 한글 키 거래 데이터 셀 (전고점매매, 전저점매매) */
function KoreanTransactionCell({ row, column, colIndex }) {
  const cellData = row[column];
  if (!cellData) return <td key={colIndex} className={TD_CLASS}>-</td>;
  return (
    <td key={colIndex} className={`${TD_CLASS} whitespace-nowrap`}>
      <div>{cellData['가격']}</div>
      {cellData['거래일'] && (
        <div className="text-xs text-gray-500">({cellData['거래일']})</div>
      )}
    </td>
  );
}

/** 한글 전고점대비 셀 */
function KoreanPeakComparisonCell({ row, column, colIndex }) {
  const percentage = row[column];
  const currentPrice = row['전고점대비현재가'];
  return (
    <td key={colIndex} className={TD_CLASS}>
      <div>{percentage !== null ? `${percentage}%` : ''}</div>
      <div className="text-xs text-gray-500">{currentPrice}</div>
    </td>
  );
}

/** 숫자 포맷 셀 */
function NumberCell({ row, column, colIndex }) {
  return (
    <td key={colIndex} className={TD_CLASS}>
      {row[column] !== null ? formatNumber(row[column]) : '-'}
    </td>
  );
}

/** 전세가율 셀 */
function JeonseRatioCell({ row, column, colIndex }) {
  return (
    <td key={colIndex} className={TD_CLASS}>
      {row[column] && row[column] !== '-' ? `${formatNumber(row[column])}%` : row[column]}
    </td>
  );
}

/** 기본 셀 */
function DefaultCell({ row, column, colIndex }) {
  return (
    <td key={colIndex} className={TD_CLASS}>
      {formatNumber(row[column])}
    </td>
  );
}

/**
 * 컬럼 이름에 따른 셀 렌더러를 반환한다.
 *
 * @param {string} column
 * @param {object} row
 * @param {number} colIndex
 * @param {{ navigate: function, onSaleClick: function, handleShowSubwayInfo: function }} handlers
 */
export function renderCell(column, row, colIndex, handlers) {
  switch (column) {
    case '매물_리스트_확인':
      return <SaleButtonsCell row={row} colIndex={colIndex} onSaleClick={handlers.onSaleClick} />;
    case 'apartment_name':
      return <ApartmentNameCell row={row} column={column} colIndex={colIndex} navigate={handlers.navigate} />;
    case 'subdistrict_name':
      return <SubdistrictCell row={row} column={column} colIndex={colIndex} handleShowSubwayInfo={handlers.handleShowSubwayInfo} />;
    case 'actual_sale_transaction_price':
      return <PriceWithDateCell row={row} column={column} dateColumn="actual_sale_transaction_date" colIndex={colIndex} />;
    case 'actual_jeonse_transaction_price':
      return <PriceWithDateCell row={row} column={column} dateColumn="actual_jeonse_transaction_date" colIndex={colIndex} />;
    case 'previous_lowest_price_price':
      return <PriceWithDateCell row={row} column={column} dateColumn="previous_lowest_price_date" colIndex={colIndex} />;
    case 'previous_peak_price_price':
      return <PriceWithDateCell row={row} column={column} dateColumn="previous_peak_price_date" colIndex={colIndex} />;
    case 'peak_comparison':
      return <PeakComparisonCell row={row} column={column} colIndex={colIndex} />;
    case '전고점매매':
    case '전저점매매':
      return <KoreanTransactionCell row={row} column={column} colIndex={colIndex} />;
    case '전고점대비':
      return <KoreanPeakComparisonCell row={row} column={column} colIndex={colIndex} />;
    case 'sale_price':
    case 'jeonse_price':
    case 'trading_gap':
      return <NumberCell row={row} column={column} colIndex={colIndex} />;
    case 'jeonse_ratio':
      return <JeonseRatioCell row={row} column={column} colIndex={colIndex} />;
    default:
      return <DefaultCell row={row} column={column} colIndex={colIndex} />;
  }
}
